import { useEffect, useState } from 'react';
import { Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

/**
 * `beforeinstallprompt` capture. Chrome / Edge fire this event only if
 * the site is eligible for install AND not already installed. We stash
 * the event, show a subtle chip in the header, and call prompt() on tap.
 *
 * iOS Safari and iOS-installed PWAs don't fire this event — they don't
 * support programmatic install. We show nothing on those platforms and
 * users continue to install via Safari's Share sheet → Add to Home Screen.
 * That's fine — this chip is a nice-to-have for the platforms that support it.
 */
export function InstallButton() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<any>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBip as any);
    window.addEventListener('appinstalled', onInstalled);

    // If launched standalone (already installed as PWA), hide entirely.
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      // iOS Safari's non-standard flag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (navigator as any).standalone === true;
    if (isStandalone) setInstalled(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip as any);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed || !deferred) return null;

  return (
    <Button
      size="xs"
      onClick={async () => {
        try {
          deferred.prompt();
          await deferred.userChoice;
        } catch { /* user cancelled */ }
        // Regardless of outcome, we can only prompt once per event.
        setDeferred(null);
      }}
      borderRadius="full"
      px={3}
      fontWeight={700}
      fontSize="11px"
      letterSpacing="0.02em"
      bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
      color="white"
      _hover={{ bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)' }}
      boxShadow="0 4px 12px rgba(233,74,143,0.35)"
      data-cta="pwa:install"
      aria-label={t('nav.install', 'Install MixGenie')}
    >
      ⬇ {t('nav.install', 'Install')}
    </Button>
  );
}
